import React, { useMemo, useState } from "react";
import { Button, Input, Popconfirm, Segmented, Tooltip } from "antd";
import {
  DeleteOutlined,
  FileTextOutlined,
  MessageOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import lang from "../helper/langHelper";

const stripHtml = (value = "") => {
  const div = document.createElement("div");
  div.innerHTML = value;
  return div.innerText || div.textContent || "";
};

// Buckets chat sessions the way ChatGPT/Claude do, so the list reads as a
// timeline instead of a flat, unlabeled stack.
const bucketFor = (dateString) => {
  const date = moment(dateString);
  const now = moment();
  if (date.isSame(now, "day")) return "Today";
  if (date.isSame(moment(now).subtract(1, "day"), "day")) return "Yesterday";
  if (date.isAfter(moment(now).subtract(7, "days"))) return "Previous 7 Days";
  return "Older";
};

const BUCKET_ORDER = ["Today", "Yesterday", "Previous 7 Days", "Older"];

const WritingChatSidebar = ({
  chatHistories = [],
  onSelectChat,
  onDeleteChat,
  onClearAll,
  onCreateNew,
  currentChatId,
  savedItems = [],
  selectedItem,
  onSelectDraft,
  onDeleteDraft,
}) => {
  const [mode, setMode] = useState("chats");
  const [searchText, setSearchText] = useState("");

  const filteredHistories = useMemo(
    () => chatHistories.filter((h) => (h.title || "").toLowerCase().includes(searchText.toLowerCase())),
    [chatHistories, searchText]
  );

  const grouped = useMemo(() => {
    const groups = {};
    filteredHistories.forEach((h) => {
      const bucket = bucketFor(h.updatedAt);
      (groups[bucket] = groups[bucket] || []).push(h);
    });
    return BUCKET_ORDER.filter((b) => groups[b]?.length).map((b) => [b, groups[b]]);
  }, [filteredHistories]);

  const filteredDrafts = useMemo(
    () => savedItems.filter((item) => (item.title || "Untitled").toLowerCase().includes(searchText.toLowerCase())),
    [savedItems, searchText]
  );

  return (
    <aside className="wa-chat-sidebar">
      <div className="wa-chat-sidebar-top">
        <Button type="primary" icon={<PlusOutlined />} block className="wa-new-chat-btn" onClick={onCreateNew}>
          {lang("New Chat")}
        </Button>
      </div>

      <div className="wa-chat-sidebar-search">
        <Input
          placeholder={lang("Search...")}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      <div className="wa-chat-sidebar-toggle">
        <Segmented
          block
          value={mode}
          onChange={setMode}
          options={[
            { label: lang("Chats"), value: "chats", icon: <MessageOutlined /> },
            { label: lang("Drafts"), value: "drafts", icon: <FileTextOutlined /> },
          ]}
        />
      </div>

      <div className="wa-chat-sidebar-list">
        {mode === "chats" ? (
          grouped.length ? (
            grouped.map(([bucket, items]) => (
              <div key={bucket} className="wa-chat-sidebar-group">
                <div className="wa-chat-sidebar-group-label">{lang(bucket)}</div>
                {items.map((history) => (
                  <div
                    key={history.id}
                    className={`wa-chat-sidebar-item${currentChatId === history.id ? " active" : ""}`}
                    onClick={() => onSelectChat(history)}
                  >
                    <MessageOutlined className="wa-chat-sidebar-item-icon" />
                    <span className="wa-chat-sidebar-item-title" title={history.title}>
                      {history.title}
                    </span>
                    <Popconfirm
                      title={lang("Delete this chat?")}
                      onConfirm={(e) => { e.stopPropagation(); onDeleteChat(history.id); }}
                      onCancel={(e) => e.stopPropagation()}
                      okText={lang("Delete")}
                      cancelText={lang("Cancel")}
                      okButtonProps={{ danger: true }}
                    >
                      <button
                        type="button"
                        className="wa-chat-sidebar-item-delete"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={lang("Delete")}
                      >
                        <DeleteOutlined />
                      </button>
                    </Popconfirm>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="wa-chat-sidebar-empty">
              {searchText ? lang("No chats found") : lang("No chat history yet")}
            </div>
          )
        ) : filteredDrafts.length ? (
          <div className="wa-chat-sidebar-group">
            {filteredDrafts.map((item) => (
              <div
                key={item._id}
                className={`wa-chat-sidebar-item${selectedItem?._id === item._id ? " active" : ""}`}
                onClick={() => onSelectDraft(item)}
              >
                <FileTextOutlined className="wa-chat-sidebar-item-icon" />
                <div className="wa-chat-sidebar-item-body">
                  <span className="wa-chat-sidebar-item-title" title={item.title}>
                    {item.title || lang("Untitled")}
                  </span>
                  <span className="wa-chat-sidebar-item-meta">
                    {item.created_at ? moment(item.created_at).format("DD MMM YYYY") : lang("Just now")}
                    {item.content && ` · ${stripHtml(item.content).trim().split(/\s+/).filter(Boolean).length} ${lang("words")}`}
                  </span>
                </div>
                {onDeleteDraft && (
                  <Popconfirm
                    title={lang("Delete this draft?")}
                    onConfirm={(e) => { e.stopPropagation(); onDeleteDraft(item); }}
                    onCancel={(e) => e.stopPropagation()}
                    okText={lang("Delete")}
                    cancelText={lang("Cancel")}
                    okButtonProps={{ danger: true }}
                  >
                    <button
                      type="button"
                      className="wa-chat-sidebar-item-delete"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={lang("Delete")}
                    >
                      <DeleteOutlined />
                    </button>
                  </Popconfirm>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="wa-chat-sidebar-empty">{lang("No saved drafts yet.")}</div>
        )}
      </div>

      {mode === "chats" && chatHistories.length > 0 && (
        <div className="wa-chat-sidebar-footer">
          <Popconfirm
            title={lang("Clear all chat history?")}
            description={lang("This will delete all your saved chats. This action cannot be undone.")}
            onConfirm={onClearAll}
            okText={lang("Clear All")}
            cancelText={lang("Cancel")}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={lang("Clear all chat history")}>
              <Button type="text" danger block size="small">
                {lang("Clear All History")}
              </Button>
            </Tooltip>
          </Popconfirm>
        </div>
      )}
    </aside>
  );
};

export default WritingChatSidebar;
